import type { SampleReport, SampleStage } from '@ctcells/contract'

import { FLOW_STAGES } from './sampleFlowModel.ts'

/**
 * **시료의 단계별 시각** — `GET /reports/{sample_id}` 의 `events[]` 에서 뽑는다.
 *
 * eric 의 화면에 시각이 하나도 없었다. 「원심 지남」이라고만 적혀 있고 **언제 지났는지**를
 * 화면이 말하지 않았다. 그 값은 서버에 있고 — 우리가 안 쓰고 있었다.
 *
 * 실측(2026-09-22 · operator · `FE-0920-05`):
 * ```
 * events: [
 *   { time: "2026-09-20T12:00:49.445004+00:00", stage: "centrifuged", module: "centrifuge-1", … },
 *   { time: "2026-09-20T12:03:51.685914+00:00", stage: "assembled",   module: "assembler-1",  … },
 *   { time: "2026-09-20T12:03:56.990272+00:00", stage: "staining",    module: "staining-1",   … },
 *   { time: "2026-09-20T12:10:18.958997+00:00", stage: "completed",   module: "staining-1",   … },
 * ]
 * ```
 *
 * ## 🔴 그 사건이 «이번 회차»의 것이라는 보장이 없다
 *
 * `app/services/reports.py` `_events()` 는 `sample_event.sample_id` 로만 거른다 —
 * **`batch_id` 로 거르지 않는다**(열은 표에 있는데 `_EVENT_COLUMNS` 가 안 뽑는다).
 * 실물이 정확히 그 함정에 빠져 있었다: 배치 `76954081…`(2026-09-21 07:23 시작)의 시료
 * `FE-0920-05` 는 `sample.stage = registered` 인데 보고서 events 는 **2026-09-20 12:10
 * completed** 다. 어제 회차의 기록이 그대로 남아 있는 것이다.
 *
 * ⇒ **이번 회차가 시작된 뒤의 사건만 쓴다**(`since = batch.started_at`).
 *   🔴 이것은 「시간창으로 귀속시키는 것」이 아니라 **제외하는 것**이다 — 틀릴 때의 결과가
 *      「모른다(`—`)」이지 「다른 회차의 시각을 이번 것이라고 단언」이 아니다. 방향이 반대다.
 *   🔴 `since` 를 모르면 **한 건도 안 쓴다.** 작업이 아직 안 시작됐으면 이번 회차의 사건이
 *      애초에 없고, 그때 옛 사건을 그리면 그게 바로 위의 거짓이다.
 * ⚠ 백엔드 요청거리: `SampleReport.events[]` 에 `batch_id` 를 실어 달라 —
 *   열이 이미 있어서 `_EVENT_COLUMNS` 한 줄이면 된다. 그러면 이 시간 거르기가 필요 없다.
 */

/** 단계 → 그 단계 표지가 찍힌 시각(ISO 문자열). 🔴 **없는 단계는 키가 없다** */
export type StageTimes = Partial<Record<SampleStage, string>>

/** 시료 → 단계별 시각. 🔴 못 본 시료는 키가 없다(빈 객체로 채우지 않는다) */
export type StageTimeLookup = ReadonlyMap<string, StageTimes>

export interface StageTimesInput {
  /** 보고서 응답. 🔴 `events` 의 항목은 계약이 `additionalProperties: true` 라 자유 객체다 */
  report: Pick<SampleReport, 'events'> | undefined
  /**
   * 🔴 **이번 회차가 시작된 시각**(`batch.started_at`). 모르면 **한 건도 안 쓴다**(위 머리말).
   */
  since: string | undefined
}

/**
 * `events[]` 한 항목에서 우리가 읽는 칸. 나머지는 계약이 모양을 정하지 않는다.
 *
 * 🔴 **`errorCode` 는 「있을 때만」 있다.** 서버가 `error_code: null` 을 싣는 것이 보통이고
 *    (`app/services/sample_lifecycle.py` 의 insert 가 그 칸을 안 쓴다), 그 `null` 은
 *    **「실패가 없었다」가 아니라 「아무 말도 안 했다」**다. 그래서 키째 없는 쪽으로 접는다 —
 *    읽는 쪽이 `null` 을 「성공」으로 오해할 여지를 타입에서 없앤다.
 */
export interface StageEvent {
  time: string
  stage: SampleStage
  errorCode?: string
}

const STAGE_SET = new Set<string>(FLOW_STAGES)

/**
 * 🔴 **자유 객체에서 «아는 모양»만 건진다.** 계약이 `additionalProperties: true` 라
 *    타입이 지켜 주지 않는다 — 실물이 `stage: null` 을 줄 수 있고(열이 nullable) 그때
 *    조용히 버려야 한다.
 */
function readEvent(raw: unknown): StageEvent | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const record = raw as Record<string, unknown>
  const time = record['time']
  const stage = record['stage']
  if (typeof time !== 'string' || typeof stage !== 'string') return undefined
  if (!STAGE_SET.has(stage)) return undefined
  const code = record['error_code']
  return {
    time,
    stage: stage as SampleStage,
    /* 🔴 빈 문자열도 「말 안 함」이다 — 빈 코드로 실패를 세우지 않는다 */
    ...(typeof code === 'string' && code.trim() !== '' ? { errorCode: code.trim() } : {}),
  }
}

/**
 * **이번 회차의 사건만** — 시각·판정 두 축이 **같은 거르기**를 쓰게 하는 한 자리.
 *
 * 🔴 두 축이 제각기 창을 계산하면 언젠가 갈린다(한쪽만 고치는 일이 생긴다). 창의 뜻은
 *    위 머리말이 전부이고, 이 함수 하나가 그것을 집행한다.
 * 🔴 **`since` 를 모르면 한 건도 안 낸다.** 「거르지 못한다」를 「전부 쓴다」로 접지 않는다.
 */
export function stageEventsSince(
  report: Pick<SampleReport, 'events'> | undefined,
  since: string | undefined,
): StageEvent[] {
  if (report === undefined || since === undefined) return []
  const sinceMs = Date.parse(since)
  if (Number.isNaN(sinceMs)) return []

  const events: StageEvent[] = []
  for (const raw of report.events) {
    const event = readEvent(raw)
    if (event === undefined) continue
    const ms = Date.parse(event.time)
    if (Number.isNaN(ms) || ms < sinceMs) continue
    events.push(event)
  }
  return events
}

/**
 * 한 시료의 단계별 시각.
 *
 * 🔴 **같은 단계가 두 번 오면 «나중» 것이다.** 재시도로 같은 표지가 두 번 찍힐 수 있고,
 *    화면이 말하려는 것은 「그 단계를 «마지막으로» 지난 때」다.
 */
export function stageTimes(input: StageTimesInput): StageTimes {
  const times: StageTimes = {}
  for (const event of stageEventsSince(input.report, input.since)) {
    const current = times[event.stage]
    if (current === undefined || Date.parse(current) <= Date.parse(event.time)) {
      times[event.stage] = event.time
    }
  }
  return times
}

/**
 * 보고서 여러 건 → 조회표. 🔴 **못 본 시료는 키가 없다** — 「0건」과 「못 봤다」를 가르기 위해서다.
 */
export function stageTimeLookup(
  reports: ReadonlyMap<string, Pick<SampleReport, 'events'>>,
  since: string | undefined,
): StageTimeLookup {
  const lookup = new Map<string, StageTimes>()
  for (const [sampleId, report] of reports) {
    lookup.set(sampleId, stageTimes({ report, since }))
  }
  return lookup
}

/**
 * **그 시료가 «마지막으로» 표지를 받은 시각.** 없으면 `undefined`.
 *
 * 🔴 **「이 단계가 시작된 때」가 아니다.** 서버는 단계 «시작» 사건을 남기지 않는다 —
 *    표지는 그 일이 끝난 자리에서 찍힌다(`sampleFlowModel.ts` 머리말의 실물 시각 비대칭).
 *    그래서 화면 문구도 「{시각} 이후 {경과}」이지 「이 단계 {경과}째」가 아니다.
 * 🔴 **단계 차례가 아니라 시각으로 고른다.** 표지가 순서를 거슬러 들어오는 일이 있어도
 *    「마지막」의 뜻이 흔들리지 않는다.
 */
export function lastStamp(times: StageTimes): string | undefined {
  let latest: string | undefined
  let latestMs = Number.NEGATIVE_INFINITY
  for (const value of Object.values(times)) {
    const ms = Date.parse(value)
    if (Number.isNaN(ms) || ms <= latestMs) continue
    latestMs = ms
    latest = value
  }
  return latest
}

/**
 * **경과 초** — 🔴 화면이 해도 되는 계산은 **시각의 뺄셈**뿐이다.
 * 「늦었다 · 지연이다」는 기준값이 서버에도 계약에도 없으므로 만들지 않는다.
 *
 * 🔴 시작 시각을 모르면 `undefined` 다. 0 으로 접으면 「방금 시작했다」는 거짓이 된다.
 * ⚠ 미래 시각(시계 어긋남)은 `undefined` 다 — 음수 경과를 화면에 적지 않는다.
 */
export function elapsedSec(from: string | undefined, now: number): number | undefined {
  if (from === undefined) return undefined
  const ms = Date.parse(from)
  if (Number.isNaN(ms)) return undefined
  const sec = Math.floor((now - ms) / 1000)
  return sec < 0 ? undefined : sec
}
